import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Heading, ScrollArea, FlyingCookie, LoadingSpinner } from '@app/shared/ui/atoms/Common';
import { NotFound } from '@app/shared/ui/molecules/Common';
import { TaskDialogMessage } from '@app/shared/ui/molecules/Tasks';
import { TasksList } from '@app/shared/ui/organisms/Tasks';
import { Modal } from '@app/shared/ui/organisms/Common';
import { useCompleteTaskMutation, useGetTasksQuery } from '@app/store/slices/tasks/tasksApi';
import { useFlyingCookie } from '@app/shared/hooks/useFlyingCookie';
import { useApiErrorHandler } from '@app/shared/hooks/useApiErrorHandler';
import { useDisplayError } from '@app/shared/hooks/useDisplayError';
import { useGetCurrentUserQuery } from '@app/store/slices/users/usersApi';
import { usePreloadImages } from '@app/shared/hooks/usePreloadImages';
import { getHeaderCookieBalanceElementRect } from '@app/shared/utils/layout';
import { telegramVibrate } from '@app/shared/utils/common';
import { TASK_ACTION } from '@app/shared/constants/tasks';
import { TaskDialogMessageType } from '@app/shared/types/pages/tasks';
import { TaskModel, TaskStatus } from '@app/store/slices/tasks/taskTypes';
import { eatingPandaImage, TASKS_PRELOAD_IMAGES } from '@app/assets';

export default function TasksPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleFetchError } = useApiErrorHandler();
  const { cookie: flyingCookie, animateCookie: animateFlyingCookie } = useFlyingCookie();

  const [messageType, setMessageType] = useState<TaskDialogMessageType | null>(null);
  const [completedTaskReward, setCompletedTaskReward] = useState<number | undefined>();
  // const [homescreenStatus, setHomescreenStatus] = useState<HomeScreenStatus>();

  const {
    data: tasksResponse,
    isLoading: isTasksLoading,
    isError: isTasksError,
  } = useGetTasksQuery();
  const tasks = tasksResponse?.tasks ?? [];
  const { data: user } = useGetCurrentUserQuery();
  const [completeTask, { isLoading: isLoadingComplete }] = useCompleteTaskMutation();
  const isImagesLoading = usePreloadImages(TASKS_PRELOAD_IMAGES);

  const failedToLoadTasksMsg = t('errors.failed_to_load_tasks');
  useDisplayError(isTasksError, failedToLoadTasksMsg);

  const handleAnimateFlyingCookie = (event: React.MouseEvent<HTMLElement>) => {
    const btnElementRect =
      event.target instanceof HTMLElement ? event.target.getBoundingClientRect() : undefined;
    const headerCookieBalanceElementRect = getHeaderCookieBalanceElementRect();
    animateFlyingCookie(btnElementRect, headerCookieBalanceElementRect);
  };

  const handleCompleteTask = async (taskId: number, event: React.MouseEvent<HTMLElement>) => {
    const taskToComplete = tasks.find((task) => task.id === taskId);

    if (!taskToComplete) {
      return;
    }
    telegramVibrate();

    try {
      const { data } = await completeTask({
        taskId,
        cookieReward: taskToComplete.cookieReward,
        xpGain: taskToComplete.xpReward,
      });

      if (data?.ok) {
        handleAnimateFlyingCookie(event);
        setMessageType('reward');
        setCompletedTaskReward(taskToComplete.cookieReward);
      }
    } catch (err) {
      handleFetchError(err);
    }
  };

  const handleAction = async (task: TaskModel, event: React.MouseEvent<HTMLElement>) => {
    const config = TASK_ACTION[task.key];

    if (config.navigateAction && task.status === TaskStatus.IN_PROGRESS) {
      config.navigateAction(navigate);
      return;
    }

    if (config.shareAction && task.status === TaskStatus.IN_PROGRESS) {
      config.shareAction(user?.id ?? 0, t('home.share_message'));
      return;
    }

    if (task.status === TaskStatus.READY && task.id) {
      await handleCompleteTask(task.id, event);
    } else if (config.messageType) {
      setMessageType(config.messageType);
    }
  };

  // const handleAddClick = () => {
  //   if (homescreenStatus === 'added') {
  //     notification.success('Начислить 20xp и 20 печенья');
  //     return;
  //   }
  //
  //   const tg = getTelegramWebApp();
  //
  //   if (tg) {
  //     tg.addToHomeScreen();
  //   }
  // };

  if (isTasksLoading || isImagesLoading) {
    return <LoadingSpinner />;
  }

  const renderTasksList = () => {
    if (isTasksError) {
      return <NotFound message={failedToLoadTasksMsg} withLightBlueBg />;
    } else if (tasks.length) {
      return (
        <TasksList
          tasks={tasks}
          onClickAction={handleAction}
          isDisabledAction={isLoadingComplete || isTasksLoading}
        />
      );
    }

    return <NotFound message={t('tasks.no_tasks')} withLightBlueBg />;
  };

  return (
    <ScrollArea>
      <Heading level={2} className="mb-5 mt-0 text-center text-xl">
        {t('tasks.title')}
      </Heading>

      {/*<Button className="mb-4 min-h-[60px]" onClick={handleAddClick}>*/}
      {/*  <Paragraph>*/}
      {/*    {homescreenStatus === 'added' ? t('common.get') : t('tasks.homescreen')}*/}
      {/*  </Paragraph>*/}
      {/*  <div className="flex items-center justify-center">*/}
      {/*    <Paragraph>20 XP / 20</Paragraph>*/}
      {/*    <Image src={cookieImage} alt="cookie image" className="w-4 min-w-4 ml-1" />*/}
      {/*  </div>*/}
      {/*</Button>*/}

      {renderTasksList()}

      <Modal
        isOpen={!!messageType}
        onOpenChange={() => {
          setMessageType(null);
        }}
        featuredImg={
          <img src={eatingPandaImage} alt="eating-panda" className="h-full w-full object-contain" />
        }
        hiddenTitle="Task Information"
      >
        <TaskDialogMessage messageType={messageType} reward={completedTaskReward} />
      </Modal>

      {flyingCookie && (
        <FlyingCookie
          startX={flyingCookie.startX}
          startY={flyingCookie.startY}
          moveToX={flyingCookie.moveToX}
          moveToY={flyingCookie.moveToY}
        />
      )}
    </ScrollArea>
  );
}
